import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  styled,
} from "@mui/material";
import { deleteActivity } from "../../../../services/scheduleService";

const ErrorButton = styled(Button)(() => ({
  background: "#DC2626",
  color: "#ffffff",
  boxShadow: "none",
  border: "1px solid #DC2626",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: 500,
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: 0,
  "&:hover": {
    borderColor: "#DC2626",
    color: "#ffffff",
    backgroundColor: "#B91C1C",
  },
  "&:disabled": {
    opacity: 0.7,
    borderColor: "#DC2626",
    color: "#ffffff",
    backgroundColor: "#DC2626",
    cursor: "not-allowed",
  },
}));

const GreyStyledButton = styled(Button)(() => ({
  background: "#fff",
  color: "#0D0D0E",
  boxShadow: "none",
  border: "1px solid #D7D8D9",
  whiteSpace: "nowrap",
  fontSize: "16px",
  fontWeight: 500,
  borderRadius: "10px",
  padding: ".5vh 1vw",
  letterSpacing: 0,
}));

const DeleteActivityModal = ({ open, onClose, activityData, onSuccess }) => {
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!open) setIsDeleting(false);
  }, [open]);

  const handleDelete = async () => {
    if (isDeleting || !activityData?.id) return;
    setIsDeleting(true);
    try {
      const response = await deleteActivity(activityData.id);
      if (response?.success) {
        toast.success("Activity deleted successfully");
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.error(response?.message || "Failed to delete activity");
        setIsDeleting(false);
      }
    } catch (error) {
      console.error("Error deleting activity:", error);
      toast.error("Failed to delete activity. Please try again.");
      setIsDeleting(false);
    }
  };

  if (!activityData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", padding: "8px" } }}
    >
      <DialogTitle
        sx={{
          fontSize: "20px",
          fontWeight: 600,
          color: "#0D0D0E",
          paddingBottom: "16px",
        }}
      >
        Delete Activity
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
            Are you sure you want to delete this activity?
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
            }}
          >
            <Box
              sx={{
                width: 45,
                height: 45,
                borderRadius: "8px",
                border: "2px solid white",
                boxShadow: "0 4px 2.67px rgba(0,0,0,0.15)",
                backgroundColor: "#e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              {activityData.milestone_image?.path ? (
                <img
                  src={activityData.milestone_image.path}
                  alt={activityData.milestone_name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : null}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 500, color: "#0D0D0E", fontSize: "14px" }}
              >
                {activityData.milestone_name || "-"}
              </Typography>
              {activityData.tags && (
                <Typography sx={{ color: "#949DAA", fontSize: "12px" }}>
                  {activityData.tags}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ padding: "16px 24px", justifyContent: "space-between" }}
      >
        <GreyStyledButton onClick={onClose} disabled={isDeleting}>
          Cancel
        </GreyStyledButton>
        <ErrorButton onClick={handleDelete} disabled={isDeleting}>
          {isDeleting ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />
              Deleting...
            </>
          ) : (
            "Delete"
          )}
        </ErrorButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteActivityModal;
